import type { ApolloCache } from '@apollo/client';
import { gql } from '../../graphql/generated/gql';

const POST_QUERY = gql(/** @complexity 4 */ `
  query updatePostSeriesCache($id: ID!) {
    post(id: $id) {
      id
      series {
        id
        name
        posts {
          id
        }
      }
    }
  }
`);

const updatePostSeriesCache =
  <
    Cache,
    NewItemKey extends string,
    NewItem extends { posts: { id: string }[] },
    Data extends { [key in NewItemKey]?: NewItem | null },
    Response extends { data?: Data | null },
  >(
    prevPosts: { id: string }[],
    newSeriesKey?: NewItemKey,
  ) =>
  (store: ApolloCache<Cache>, { data }: Response) => {
    const series = newSeriesKey && data ? data[newSeriesKey] : null;
    const currentPosts = series?.posts || [];

    const currentPostIds = new Set(currentPosts.map((post) => post.id));
    const prevPostIds = new Set(prevPosts.map((post) => post.id));

    const allPostIds = new Set([...prevPostIds, ...currentPostIds]);
    allPostIds.forEach((id) => {
      const isAdded = currentPostIds.has(id) && !prevPostIds.has(id);
      const isRemoved = !currentPostIds.has(id) && prevPostIds.has(id);

      if (isAdded || isRemoved) {
        const options = { query: POST_QUERY, variables: { id } };

        try {
          const prev = store.readQuery(options);
          const post = { ...prev?.post, series: isAdded ? series : null };
          // @ts-expect-error
          store.writeQuery({ ...options, data: { post } });
        } catch (ignored) {
          // post was never queried so no result in store yet
        }
      }
    });
  };

export default updatePostSeriesCache;
